import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@stashinvest/ui';
import MoneyCoach from 'stash/components/ai/chat/MoneyCoach';
import SignUpGraph from 'stash/graph/SignUpGraph';
import UniversalOnboardingGraph from './UniversalOnboardingGraph';
import {
	Navbar,
	SignUpContent,
	SignUpLayout,
	SettingsNavBar,
} from 'stash/components/global';
import { useAnalytics } from 'stash/hooks/useAnalytics';
import { useTrack } from 'stash/hooks/useTrack';
import { useLocation } from 'react-router-dom';
import {
	getEntitlementFlagsSelector,
	profileSelector,
	userSelector,
} from 'stash/selectors';
import DefaultAvatar from 'stash/assets/svg/default-avatar.svg';
import { getSearchParameters } from '../utils';
import { setIdvReturnParam } from 'stash/actions';
import { RETURN_TO_KEY, RETURN_TO_VALUE } from 'stash/constants/identityVerification';
import { isMobileWeb, isNativeWebview } from 'stash/utils';
import { EXPERIMENTS, useIsInExperimentVariation } from 'stash/components/experiments';
import SettingsNavBarVariation from 'stash/components/global/SettingsNavBarVariation/SettingsNavBarVariation';

const AuthenticatedOnboardingAppUI = ({ ready, theme, product, uuid }) => {
	const analytics = useAnalytics();
	const track = useTrack();
	const location = useLocation();
	const user = useSelector(userSelector);
	const profile = useSelector(profileSelector);
	const entitlementFlags = useSelector(getEntitlementFlagsSelector);
	const dispatch = useDispatch();

	const isInStateZeroRedesignVariation = useIsInExperimentVariation(
		EXPERIMENTS.STATE_ZERO_REDESIGN,
		true
	);
	const isHeaderHiddenOnMobile = useIsInExperimentVariation(
		EXPERIMENTS.HIDE_HEADER_ON_MOBILE,
		true
	);
	if (getSearchParameters(RETURN_TO_KEY) === RETURN_TO_VALUE) {
		dispatch(setIdvReturnParam(true));
	}

	useEffect(() => {
		if (ready && uuid) {
			void analytics.identify(uuid, {}, { context: { stash_clean: true } });
		}
	}, [ready, uuid, analytics, track]);

	const graph = UniversalOnboardingGraph;

	const hideNavbar = isNativeWebview() || (isHeaderHiddenOnMobile && isMobileWeb());

	// TODO Remove this isBlockedFromStateZero if the new state zero redesign wins
	const isBlockedFromStateZero =
		entitlementFlags?.hasPersonalEnititlement && !entitlementFlags?.hasSmartEntitlement;
	const shouldShowMarketReport = entitlementFlags?.hasPremiumMarketReportEntitlement;

	const shouldShowSettingsHeader =
		location.pathname === '/sign-up/state-zero' ||
		// Users who are not able to access state zero will still need to access the settings
		// nav on these checkpoint pages so they can cancel their subscription
		(isBlockedFromStateZero &&
			(location.pathname === '/sign-up/state-zero-primer' ||
				location.pathname === '/sign-up/investment-selection'));

	return (
		<ThemeProvider mode={theme.name}>
			{shouldShowSettingsHeader ? (
				<>
					{isInStateZeroRedesignVariation ? (
						<SettingsNavBarVariation
							user={user}
							showMarketReport={shouldShowMarketReport}
						/>
					) : (
						<SettingsNavBar
							user={user}
							avatar={profile?.avatar_url || DefaultAvatar}
							showMarketReport={shouldShowMarketReport}
						/>
					)}
				</>
			) : (
				<>
					{!hideNavbar && <Navbar />}
					<MoneyCoach />
				</>
			)}
			<SignUpLayout>
				<SignUpContent>
					{ready && <SignUpGraph product={product} graph={graph} />}
				</SignUpContent>
			</SignUpLayout>
		</ThemeProvider>
	);
};

AuthenticatedOnboardingAppUI.propTypes = {
	hasPurchaseOrder: PropTypes.bool,
	theme: PropTypes.object.isRequired,
	uuid: PropTypes.string,
};

export default AuthenticatedOnboardingAppUI;
